import React, { useState, useMemo } from 'react';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useNormalisations, useEbitdaSummary, useDeleteNormalisation } from '@/hooks/use-financial-data';
import { AddEbitdaAdjustmentModal } from '../modals/add-ebitda-adjustment-modal';
import { cn } from '@/lib/utils';
import { fromCents, toCents } from '@shared/calc/money';

export interface EbitdaNormalizationPanelProps {
  periodId: string | undefined;
}

/**
 * EBITDA Normalisation Panel
 *
 * F010 - Sprint 6A
 * Displays EBITDA summary with addbacks/removals and manages normalisation adjustments.
 * Includes what-if toggle for client-side calculation simulation.
 */
export const EbitdaNormalizationPanel: React.FC<EbitdaNormalizationPanelProps> = ({ periodId }) => {
  const { data: adjustments = [], isLoading, isError: adjustmentsError } = useNormalisations(periodId);
  const { data: summary, isLoading: summaryLoading, isError: summaryError } = useEbitdaSummary(periodId);
  const deleteMutation = useDeleteNormalisation();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // What-if state: track which adjustments are "disabled" for calculation purposes
  // Key is adjustment ID, value is whether it's enabled (true = included in calc)
  const [whatIfState, setWhatIfState] = useState<Record<string, boolean>>({});

  const excludedReasons = useMemo(() => new Map(
    (summary?.excludedAdjustments ?? []).map(row => [row.id, row.reason])
  ), [summary]);

  // What-if only reverses disabled, accepted rows from the server's totals.
  // With all toggles on, the server's adjusted EBITDA is displayed unchanged.
  const calculatedTotals = useMemo(() => {
    if (!summary) return null;
    let totalAddbacks = toCents(summary.totalAddbacks);
    let totalRemovals = toCents(summary.totalRemovals);
    let adjustedEbitda = toCents(summary.adjustedEbitda);
    for (const adj of summary.adjustments) {
      if (excludedReasons.has(adj.id) || whatIfState[adj.id] !== false) continue;
      const amount = toCents(adj.amount);
      if (adj.direction === 'add') {
        totalAddbacks -= amount;
        adjustedEbitda -= amount;
      } else if (adj.direction === 'remove') {
        totalRemovals -= amount;
        adjustedEbitda += amount;
      }
    }

    return {
      baseEbitda: Number(summary.baseEbitda),
      totalAddbacks: fromCents(totalAddbacks),
      totalRemovals: fromCents(totalRemovals),
      adjustedEbitda: fromCents(adjustedEbitda),
    };
  }, [summary, excludedReasons, whatIfState]);

  const handleWhatIfToggle = (adjustmentId: string) => {
    setWhatIfState(prev => ({
      ...prev,
      [adjustmentId]: prev[adjustmentId] === false ? true : false, // Toggle between enabled/disabled
    }));
  };

  const handleDeleteClick = (id: string) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmId && periodId) {
      await deleteMutation.mutateAsync({ id: deleteConfirmId, periodId });
      setDeleteConfirmId(null);
    }
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getAdjustmentName = (adj: any) => {
    return adj.customName || adj.template?.name || adj.adjustmentName || 'Unknown';
  };

  if (adjustmentsError || summaryError) {
    return <div role="alert" data-testid="panel-ebitda-normalisation">Could not load EBITDA normalisation. Please try again.</div>;
  }

  if (isLoading || summaryLoading) {
    return (
      <div className="flex items-center justify-center py-8" data-testid="panel-ebitda-normalisation">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!calculatedTotals) {
    return <div data-testid="panel-ebitda-normalisation">Select a financial period to view EBITDA normalisation.</div>;
  }

  return (
    <>
      <div data-testid="panel-ebitda-normalisation">
        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">Base EBITDA</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-semibold" data-testid="summary-base-ebitda">
                ${formatCurrency(calculatedTotals.baseEbitda)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">Total Addbacks</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-semibold text-green-600" data-testid="summary-total-addbacks">
                ${formatCurrency(calculatedTotals.totalAddbacks)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">Total Removals</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-semibold text-red-600" data-testid="summary-total-removals">
                ${formatCurrency(calculatedTotals.totalRemovals)}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xs font-medium text-muted-foreground">Adjusted EBITDA</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-lg font-semibold text-blue-600" data-testid="summary-adjusted-ebitda">
                ${formatCurrency(calculatedTotals.adjustedEbitda)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Adjustments Table */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">EBITDA Normalisation Adjustments</CardTitle>
              <Button
                variant="default"
                size="sm"
                onClick={() => setAddModalOpen(true)}
                disabled={!periodId}
                data-testid="button-add-adjustment"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Adjustment
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {adjustments.length === 0 && excludedReasons.size === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center border rounded-md" data-testid="empty-state-message">
                <p className="text-sm text-muted-foreground">No normalisation adjustments</p>
                <p className="text-xs text-muted-foreground mt-1">Click "Add Adjustment" to create one</p>
              </div>
            ) : (
              <div className="border rounded-md" data-testid="table-adjustments">
                <Table>
                  <TableHeader>
                    <TableRow className="h-10 bg-muted/50">
                      <TableHead className="text-xs font-medium px-2 py-1">Name</TableHead>
                      <TableHead className="text-xs font-medium px-2 py-1">Type</TableHead>
                      <TableHead className="text-xs font-medium px-2 py-1">Direction</TableHead>
                      <TableHead className="text-xs font-medium px-2 py-1 text-right">Amount</TableHead>
                      <TableHead className="text-xs font-medium px-2 py-1 text-center">What-If</TableHead>
                      <TableHead className="text-xs font-medium px-2 py-1">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  {[
                    { label: 'Included', rows: adjustments.filter(adj => !excludedReasons.has(adj.id)) },
                    { label: 'Excluded', rows: summary?.excludedAdjustments ?? [] },
                  ].map(group => <TableBody key={group.label}>
                    {group.label === 'Excluded' && group.rows.length > 0 && (
                      <TableRow><TableCell colSpan={6}>Excluded</TableCell></TableRow>
                    )}
                    {group.rows.map((adj: any) => {
                      const exclusionReason = excludedReasons.get(adj.id);
                      const isEnabled = whatIfState[adj.id] !== false;
                      const adjustmentName = getAdjustmentName(adj);
                      return (
                        <TableRow
                          key={adj.id}
                          data-testid={`row-adjustment-${adj.id}`}
                          className={cn(
                            'h-10',
                            !exclusionReason && !isEnabled && 'disabled opacity-50 line-through'
                          )}
                        >
                          <TableCell className="px-2 py-1 text-sm">
                            {adjustmentName}
                            {exclusionReason && <p className="text-xs text-muted-foreground">{exclusionReason}</p>}
                          </TableCell>
                          <TableCell className="px-2 py-1">
                            <Badge
                              variant="outline"
                              data-testid={`badge-type-${adj.adjustmentType}`}
                            >
                              {adj.adjustmentType?.replace(/_/g, ' ')}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-2 py-1">
                            <Badge
                              variant={adj.direction === 'add' ? 'default' : 'destructive'}
                              className={adj.direction === 'add' ? 'bg-green-500' : ''}
                              data-testid={`badge-direction-${adj.direction}`}
                            >
                              {adj.direction === 'add' ? 'Add' : 'Remove'}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-2 py-1 text-sm text-right">
                            ${formatCurrency(typeof adj.amount === 'string' ? parseFloat(adj.amount) : adj.amount)}
                          </TableCell>
                          <TableCell className="px-2 py-1 text-center">
                            {!exclusionReason && <Switch
                              checked={isEnabled}
                              onCheckedChange={() => handleWhatIfToggle(adj.id)}
                              data-testid={`switch-whatif-${adj.id}`}
                            />}
                          </TableCell>
                          <TableCell className="px-2 py-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(adj.id)}
                              disabled={deleteMutation.isPending}
                              data-testid={`button-delete-${adj.id}`}
                            >
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>)}
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Add Adjustment Modal */}
      {periodId && (
        <AddEbitdaAdjustmentModal
          open={addModalOpen}
          periodId={periodId}
          onClose={() => setAddModalOpen(false)}
          onSuccess={() => setAddModalOpen(false)}
        />
      )}

      {/* Delete Confirmation Dialog - explicit role="dialog" for Playwright compatibility */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent data-testid="dialog-delete-confirm" role="dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Adjustment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this adjustment? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              data-testid="button-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
